// ── The user's space: felt resonance, and the frozen record of an arrangement ──
//
// Everything here is a pure function of coordinates. Nothing in this file knows
// what a reading is, and nothing in it may ever be handed to the model: this is
// one of the two spaces, and the whole claim of the instrument is that the other
// one was built without it.

// Felt resonance is DERIVED, never stored. The same coordinates in the same
// field must always yield the same value. v5 stored it on the fragment and
// recomputed the list on every drop, so a fragment could carry a number its own
// position no longer justified — the seed fragments all did.
export function feltResonance(x, y, cx, cy) {
  const maxD = Math.hypot(cx, cy);
  if (!maxD) return 0.1;
  return Math.max(0.1, +(1 - Math.min(Math.hypot(x - cx, y - cy) / maxD, 1)).toFixed(2));
}

// ── 3-1. Freeze ────────────────────────────────────────────────────
// A reading belongs to the arrangement that was on the canvas when it was asked
// for, not to wherever the fragments end up afterwards. Comparison is always
// frozen snapshot against that reading; the live canvas is never one side of
// the comparison, or the user's later moves anchor the result.
//
// The snapshot is stored NORMALISED — (x/w, y/h) plus the field it was taken in
// — because Phase 1 made felt resonance a derived value and the field centre a
// ResizeObserver reading. Frozen as pixels, the same record would yield a
// different resonance and a different proximity order in a different window,
// and 3-3's divergence would flip with nothing edited.
export function freezeArrangement(frags, field) {
  const snapshot = {
    frozenAt: new Date().toISOString(),
    field: { w: field.w, h: field.h },
    // v6.1. A fragment that already carries its normalised position is stored
    // from that value rather than from its pixels. The two agree, but they are
    // not bit-identical: pixels are nx*w, and nx*w/w comes back off by a float
    // ulp often enough to change an id. Dividing was the only way to get here
    // when pixels were the state; a fragment that knows its own nx should not
    // have it re-derived through the window it happens to be drawn in.
    //
    // The fallback is the original expression, and it is what every caller
    // holding plain pixels still gets.
    positions: Object.fromEntries(
      frags.map(f => [f.id, {
        nx: f.nx ?? f.x / field.w,
        ny: f.ny ?? f.y / field.h,
      }])
    ),
  };
  return { id: arrangementId(snapshot), ...snapshot };
}

// The id names the ARRANGEMENT, not the moment. It is derived from the geometry
// alone, so freezing an untouched canvas twice gives the same id — which is the
// true statement, and lets a record show that two readings were taken of one
// arrangement. `frozenAt` is what names the moment; the two answer different
// questions and neither is a substitute for the other.
//
// Deliberately synchronous: freeze happens on the click, before any await, and
// an id that arrives a tick later is an id that can be missing when the reading
// is committed. FNV-1a is enough for a label — nothing here is a security claim.
export function arrangementId(snapshot) {
  const material = JSON.stringify({
    field: snapshot.field,
    // sorted, so id depends on the arrangement and not on insertion order
    positions: Object.keys(snapshot.positions).sort()
      .map(k => [k, snapshot.positions[k].nx, snapshot.positions[k].ny]),
  });
  let h = 0x811c9dc5;
  for (let i = 0; i < material.length; i++) {
    h ^= material.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

// Normalised is canonical; pixels are derived. Which field you reproject into
// decides what the pixels are FOR:
//   the snapshot's own field → measurement. The geometry the user authored,
//                              reproduced identically on any screen.
//   the live field           → display. Where those points sit on this canvas.
export function projectFrozen(snapshot, into) {
  return Object.entries(snapshot.positions)
    .map(([id, p]) => ({ id, x: p.nx * into.w, y: p.ny * into.h }));
}

// Every measurement over a frozen arrangement goes through here, in the field
// the arrangement was made in. Nothing downstream may reach for a live
// coordinate: 3-3's ranks and distances read this, not `frags`.
//
// Note what this does NOT do: measure in the unit square. Normalising divides x
// by the width and y by the height, so on a non-square field the unit square is
// the authored geometry with one axis stretched — near pairs and far pairs can
// swap. The record is normalised for portability; the measurement is taken
// after putting the aspect ratio back.
export function frozenGeometry(snapshot) {
  const points = projectFrozen(snapshot, snapshot.field);
  const cx = snapshot.field.w / 2, cy = snapshot.field.h / 2;
  return {
    points,
    cx, cy,
    resonanceAt: (p) => feltResonance(p.x, p.y, cx, cy),
    distance: (a, b) => Math.hypot(a.x - b.x, a.y - b.y),
  };
}

// Has the user changed the arrangement since it was frozen? Compared in PIXELS
// against the pixels the snapshot was taken at — deliberately not normalised
// against normalised. A window resize moves the field, not the fragments: the
// user did not touch the arrangement, so it must not raise the notice. Half a
// pixel of float noise is not a move either.
export const FROZEN_EPS_PX = 0.5;
// v6.1. The comparison moved into normalised space, and the threshold did not
// move with it: the difference is still multiplied back by the field the
// snapshot was taken in, so "half a pixel is not a move" means the same pixel
// it always did.
//
// For a fragment carrying only pixels the fallback is the previous expression
// exactly — |f.x/w - p.nx| * w is |f.x - p.nx*w| — so nothing about what counts
// as a change was redefined here. What changed is which fragments have an nx to
// read: the app now keeps one, and a fragment whose pixels were re-derived
// after a resize no longer compares its new pixels against the old field's.
export function arrangementChanged(snapshot, frags) {
  if (!snapshot) return false;
  const { w, h } = snapshot.field;
  if (Object.keys(snapshot.positions).length !== frags.length) return true;
  return frags.some(f => {
    const p = snapshot.positions[f.id];
    if (!p) return true; // a fragment the reading never saw
    const fnx = f.nx ?? f.x / w, fny = f.ny ?? f.y / h;
    return Math.abs(fnx - p.nx) * w > FROZEN_EPS_PX
        || Math.abs(fny - p.ny) * h > FROZEN_EPS_PX;
  });
}

// The arrangement without the window it was seen through. arrangementId hashes
// the field as well, deliberately: a record names a measurement, and the field
// is what the measurement was taken in. But the same hand arrangement viewed on
// two screens is the same arrangement, and there was no way to say so — session
// 01's five freezes could only be matched because that session never resized.
//
// Additive and separate. arrangementId keeps its inputs and its values, so
// every id already written still means what it meant.
export function contentArrangementId(snapshot) {
  const material = JSON.stringify(
    Object.keys(snapshot.positions).sort()
      .map(k => [k, snapshot.positions[k].nx, snapshot.positions[k].ny]),
  );
  let h = 0x811c9dc5;
  for (let i = 0; i < material.length; i++) {
    h ^= material.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0").slice(0, 8);
}
